import { Accessor, Setter, Signal } from "solid-js";

export function createSetter<T>(setFunction: (value: T) => void): Setter<T> {
    let value: T;

    const setter: Setter<T> = <U extends T>(input?: Exclude<U, Function> | ((prev: T) => U) | []): U => {
        if (typeof input === 'function') {
            value = (input as (prev: T) => U)(value);
            setFunction(value);
            return value as U;
        }
        
        value = input as Exclude<U, Function>;
        setFunction(value)
        return value as U;
    };

    return setter;
}

/**
 * This method allows you to create a new signal from a getter and setter function. This is useful for taking a value from a store and converting it to a signal.
 * 
 * const [store, setStore] = createStore({ a: 1 });
 * const [a, setA] = createProxySignal(() => store.a, (value) => setStore('a', value));
 * 
 * This is also useful if the type of a signal is not the same as the type of a prop that you want to pass to a component.
 * Let's say you have a component:
 * 
 * function MyComponent({ value: Signal<string> }) { ... }
 * 
 * And you have a signal:
 * 
 * const [value, setValue] = createSignal<number>(1);
 * 
 * You can create a proxy signal like this:
 * 
 * const [strValue, setStrValue] = createProxySignal(() => `${value()}`, (v) => setValue(parseInt(v)));] 
 *
 * and pass it to the component
 * 
 * <MyComponent value={[strValue, setStrValue]} />
 * 
 * and the original signal will be updated when the proxy signal is updated.
 */
export function createProxySignal<T>(accessor: Accessor<T>, setter: (value: T) => void): Signal<T> {
    return [accessor, createSetter(setter)]
}
